// ─────────────────────────────────────────────────────────────────────────────
// THE DASHBOARD'S NOTES TILE: its list and its popup (Carla's item 6, 2026-10-12).
//
// The 3 most recently edited notes (it was the first 5 in load order). A note opened from the tile
// opens in the v2 kit Modal with the Notes page's own note view (NoteSheet, from SharedNote.jsx).
// It replaces App's old hand-styled modal, which showed only the note's plain `text`, so a note
// written on the Notes page (whose writing is in its blocks) opened blank. In the popup:
//   Open in Notes   the Notes page, opened on that note (deep link entity 'note'), to edit it
//   Delete          waits behind Undo (useDelayedDelete, decision 7); a page with pages inside is
//                   deleted from Notes, where it is asked about first
//
// Round 3, item 6 REDO (Carla's live check of 76690ab): the tile still read as the quick-note
// composer, because a big text box, a category picker and "+ Save note" sat under the list. Now
// (NotesTile): the 3 most recent notes, each row its title, a ONE-line preview from the note's own
// blocks (or its text) and its date; "View all" kept; quick capture behind a small "+ New note"
// link in the tile's header, which opens the composer in the kit Modal. The composer closes only
// once the server has the note, and a refusal stays open with the server's words (App's addNote,
// used before, added the note on a guess and only logged a failure).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { api } from '../../api/client';
import { setDeepLink } from '../../utils/deepLink';
import { formatNoteDate } from '../../utils/dateUtils';
import { messageFromError } from '../../utils/apiError';
import { Banner, Button, DashboardCard, Field, HeaderAction, Input, Modal, Select, Textarea } from '../../ui';
import { blocksToText, newNoteId } from './BlockEditor';

const editedAt = (n) => n?.updated || n?.created;
const time = (iso) => { const t = Date.parse(iso || ''); return Number.isNaN(t) ? 0 : t; };

// The n most recently edited notes, newest first.
export function recentNotes(notes, n = 3) {
  return [...(notes || [])].sort((a, b) => time(editedAt(b)) - time(editedAt(a))).slice(0, n);
}

function label(note) {
  if (note.title?.trim()) return note.title.trim();
  const words = (note.text || '').trim().split(/\s+/).filter(Boolean);
  return words.length ? words.slice(0, 6).join(' ') + (words.length > 6 ? '…' : '') : 'Untitled note';
}

// A row's title and its one-line preview, from the note's own writing: its blocks (what the Notes
// page writes) or, when it has none, its plain text (what quick capture writes). A note with no
// title takes its first line as the title and the rest as the preview.
const PREVIEW_MAX = 160;
export function noteRow(note) {
  const fromBlocks = Array.isArray(note?.blocks) && note.blocks.length ? blocksToText(note.blocks) : '';
  const body = fromBlocks.trim() ? fromBlocks : (note?.text || '');
  const lines = body.split('\n').map((s) => s.replace(/\s+/g, ' ').trim()).filter(Boolean);
  const cap = (s) => (s.length > PREVIEW_MAX ? `${s.slice(0, PREVIEW_MAX - 1)}…` : s);
  if (note?.title?.trim()) return { title: note.title.trim(), preview: cap(lines.join(' ')) };
  if (!lines.length) return { title: 'Untitled note', preview: '' };
  return { title: cap(lines[0]), preview: cap(lines.slice(1).join(' ')) };
}

const imageLink = async (fileId) => {
  const data = await api(`/api/files/${encodeURIComponent(fileId)}/link`).then((r) => r.json());
  if (!data?.url) throw new Error('This picture could not be opened.');
  return data;
};

// notes: App's notes; categories: the note categories; onOpenNotes(): go to the Notes page.
// Decision 90 (Carla, 2026-10-12): "The Dashboard Notes tile and every link to a note open the new
// editor view." A row opens the note in the Notes editor (the deep link 'note'); the popup it opened
// before (with its own Delete) is gone: delete is in the editor and in the card's menu, behind Undo.
export function RecentNotes({ notes = [], categories = [], onOpenNotes }) {
  const shown = recentNotes(notes);
  const open = (id) => {
    setDeepLink('note', id);
    if (onOpenNotes) onOpenNotes();
  };

  return shown.length === 0 ? (
    <p className="dash-text">No notes yet. Press + New note to write one.</p>
  ) : (
    <ul className="dash-notes-list">
      {shown.map((note) => {
        const { title, preview } = noteRow(note);
        const cat = categories.find((c) => c.id === note.categoryId);
        const when = editedAt(note) ? formatNoteDate(editedAt(note)) : '';
        // The title and preview are cut to one line each; the row's name and hover title keep the whole
        // title, so it can always be read (a screen reader hears it; a mouse sees it on hover).
        const name = [title, preview, when].filter(Boolean).join(', ');
        return (
          <li key={note.id}>
            <button type="button" data-testid="dash-note" className="dash-note" onClick={() => open(note.id)} aria-label={name} title={title}>
              <span className="dash-note__main">
                <span className="dash-note__label">
                  {/* The category's own colour, chosen by the user: data, not a style. */}
                  {cat && <span className="dash-swatch" style={{ background: cat.colour }} role="img" aria-label={cat.name} />}
                  <span className="dash-note__title">{title}</span>
                </span>
                {preview && <span className="dash-note__preview">{preview}</span>}
              </span>
              {when && <span className="dash-note__date">{when}</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// The colours a new category can take (moved here with the composer from Dashboard.js).
export const PRESET_COLOURS = ['#b0bec5', '#E4E69B', '#4fc3f7', '#81c784', '#ef9a9a', '#ce93d8', '#ffcc80', '#80cbc4'];

// Quick capture, in the kit Modal. Closes only once the server has the note; a refusal stays open
// with what was written and the server's words. A new category is made here too.
export function NewNoteComposer({ open, onClose, categories = [], setNoteCategories, onCreated }) {
  const [text, setText] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newCat, setNewCat] = useState(null);   // { name, colour, saving, error } while making one

  const reset = () => { setText(''); setCategoryId(''); setSaving(false); setError(''); setNewCat(null); };
  const close = () => { if (saving) return; reset(); onClose(); };

  const save = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    setError('');
    const note = { id: newNoteId(), title: '', text: text.trim(), blocks: [], created: new Date().toISOString(), categoryId: categoryId || null };
    try {
      const res = await api('/api/notes', { method: 'POST', body: JSON.stringify(note) });
      const data = await res.json();
      reset();
      onClose();
      if (onCreated) onCreated(data?.note || note);
    } catch (err) {
      setSaving(false);
      setError(messageFromError(err, 'That note could not be saved.'));
    }
  };

  const createCategory = async () => {
    if (!newCat?.name.trim() || newCat.saving) return;
    setNewCat((c) => ({ ...c, saving: true, error: '' }));
    try {
      const res = await api('/api/note-categories', { method: 'POST', body: JSON.stringify({ name: newCat.name.trim(), colour: newCat.colour }) });
      const data = await res.json();
      if (!data?.category) throw new Error('That category could not be made.');
      if (setNoteCategories) setNoteCategories((prev) => [...(prev || []), data.category]);
      setCategoryId(data.category.id);
      setNewCat(null);
    } catch (err) {
      setNewCat((c) => ({ ...c, saving: false, error: messageFromError(err, 'That category could not be made.') }));
    }
  };

  return (
    <Modal open={open} onClose={close} title="New note" color="pastel-pink"
      footer={(
        <>
          <Button variant="tertiary" onClick={close} disabled={saving}>Cancel</Button>
          <Button variant="confirm" onClick={save} disabled={!text.trim() || saving} aria-busy={saving || undefined}>
            {saving ? 'Saving…' : 'Save note'}
          </Button>
        </>
      )}>
      <div className="dash-composer" data-testid="dash-note-composer">
        {error && <Banner tone="problem">{error}</Banner>}
        <Field label="Note">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} autoFocus placeholder="Write a quick note…" />
        </Field>
        <Field label="Category">
          <Select value={newCat ? '__new__' : categoryId}
            onChange={(e) => {
              if (e.target.value === '__new__') setNewCat({ name: '', colour: PRESET_COLOURS[0], saving: false, error: '' });
              else { setCategoryId(e.target.value); setNewCat(null); }
            }}>
            <option value="">No category</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            <option value="__new__">＋ New category…</option>
          </Select>
        </Field>
        {newCat && (
          <div className="dash-newcat">
            <Field label="Category name" error={newCat.error || undefined}>
              <Input value={newCat.name} onChange={(e) => setNewCat((c) => ({ ...c, name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createCategory(); } }} />
            </Field>
            <div className="dash-swatches" role="group" aria-label="Category colour">
              {PRESET_COLOURS.map((colour) => (
                <button key={colour} type="button" onClick={() => setNewCat((c) => ({ ...c, colour }))}
                  className="dash-swatch-btn ui-hit" aria-pressed={newCat.colour === colour} aria-label={`Colour ${colour}`}
                  style={{ background: colour }} />
              ))}
            </div>
            <div className="dash-actions">
              <Button variant="confirm" size="sm" onClick={createCategory} disabled={!newCat.name.trim() || newCat.saving}>Create</Button>
              <Button variant="tertiary" size="sm" onClick={() => setNewCat(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// The Dashboard's Notes tile: the list, "+ New note" and "View all" in its header strip.
export function NotesTile({ notes = [], categories = [], setNoteCategories, onOpenNotes, onDeleted, onCreated }) {
  const [composing, setComposing] = useState(false);
  return (
    <DashboardCard tint="pastel-pink" title="Notes" meta={(
      <>
        <HeaderAction onClick={() => setComposing(true)} data-testid="dash-note-new">+ New note</HeaderAction>
        {notes.length > 0 && <HeaderAction onClick={() => onOpenNotes && onOpenNotes()}>View all →</HeaderAction>}
      </>
    )}>
      <RecentNotes notes={notes} categories={categories} onOpenNotes={onOpenNotes} />
      <NewNoteComposer open={composing} onClose={() => setComposing(false)} categories={categories}
        setNoteCategories={setNoteCategories} onCreated={onCreated} />
    </DashboardCard>
  );
}

export default RecentNotes;
